import { Fragment, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { common, profile } from 'lang/en.json';
import { addLogo, companySelector, updateCompanyById } from 'store/company';
import { authSelector } from 'store/auth';
import { hidePopup, setPopupConfig, showPopup } from 'store/ux';
import { escapeHtml } from 'utils/safe-html';
import Button from 'components/Button';
import Divider from 'components/Divider';
import Typo from 'components/Typo';
import LimitedTextArea from './LimitedTextArea';
import Details from './Details';
import FcaDetails from './FcaDetails';
import SidescreenHeader from './SidescreenHeader';
import Social from './Social';

const EditCompanyInfo = ({ onUpdate, title, isJournalist }) => {
    const dispatch = useDispatch();
    const { data } = useSelector(companySelector);
    const { company } = useSelector(authSelector);

    const { register, handleSubmit, errors } = useForm();

    const [companyLogo, setCompanyLogo] = useState(null);
    const [fcaDirty, setFcaDirty] = useState(false);
    const [companyLogoPreview, setCompanyLogoPreview] = useState(
        data.logo ? data.logo.path : null
    );

    const [formData, setFormData] = useState({
        details: {
            name: data.name,
            website: data.website,
            email: data.email,
            phone: data.phone,
            publication: data.publication,
            logo: data.logo && data.logo.path,
            employer: data.employer,
            position: data.position,
        },
        socialNetwork: data.socialNetwork,
        description: data.description,
        logo: data.logo && data.logo.path,
    });

    const handleDetails = (details) => {
        setFormData({ ...formData, details });
    };

    const handleSocial = (socialNetwork) => {
        setFormData({ ...formData, socialNetwork });
    };

    const handleDescription = (description) => {
        setFormData({ ...formData, description });
    };

    const handleFile = (file) => {
        setCompanyLogo(file.file);
        setCompanyLogoPreview(file.src);
    };

    const handleFileUpload = async () => {
        if (!companyLogo) {
            if (onUpdate) {
                return onUpdate();
            }
            return null;
        }

        const formData = new FormData();
        formData.append('logo', companyLogo);

        dispatch(addLogo(formData));

        if (!data.loading && onUpdate) {
            onUpdate();
        }
    };

    const save = async () => {
        const { details, description, socialNetwork } = formData;
        dispatch(updateCompanyById({ ...details, description, socialNetwork }));
        await handleFileUpload();
    };

    // Saving the rest closes the side screen, which would silently drop unsaved FCA edits.
    const confirmFcaDiscard = (proceed) => {
        if (!fcaDirty) return proceed();
        dispatch(showPopup());
        dispatch(
            setPopupConfig({
                title: 'Your FCA details are not saved',
                body: 'They save separately, with Save FCA details. Save the rest and discard the FCA changes?',
                confirmTitle: 'Discard FCA changes',
                cancelTitle: 'Keep editing',
                onCancel: () => dispatch(hidePopup()),
                onConfirm: () => {
                    dispatch(hidePopup());
                    proceed();
                },
            })
        );
    };

    const handleFormSubmit = (e) => {
        e.preventDefault();
        confirmFcaDiscard(confirmContactChange);
    };

    const confirmContactChange = () => {
        const changed = ['email', 'phone'].filter(
            (field) => (data[field] || '') !== (formData.details[field] || '')
        );
        if (data.slug === company || !changed.length) {
            return save();
        }

        dispatch(showPopup());
        dispatch(
            setPopupConfig({
                title: `Change ${data.name}'s contact details?`,
                body: changed
                    .map(
                        (field) =>
                            `${common[field]}: ${escapeHtml(
                                data[field] || 'none'
                            )} → ${escapeHtml(
                                formData.details[field] || 'none'
                            )}`
                    )
                    .join('<br>'),
                confirmTitle: common.save,
                cancelTitle: 'Cancel',
                onCancel: () => dispatch(hidePopup()),
                onConfirm: () => {
                    dispatch(hidePopup());
                    save();
                },
            })
        );
    };

    return (
        <Fragment>
            <form
                onSubmit={(e) => handleSubmit(handleFormSubmit(e))}
                autoComplete="off"
            >
                <SidescreenHeader title={title} />

                <Details
                    callback={handleDetails}
                    register={register}
                    errors={errors}
                    data={formData.details}
                    handleFile={handleFile}
                    logoPreview={companyLogoPreview}
                    isJournalist={isJournalist}
                />

                <Divider />

                <Social
                    socialNetwork={formData.socialNetwork}
                    callback={(val) => handleSocial(val)}
                />

                <Divider />

                <LimitedTextArea
                    isJournalist={isJournalist}
                    callback={(val) => handleDescription(val)}
                    register={register}
                    errors={errors}
                    value={formData.description}
                    description={
                        isJournalist
                            ? profile.header.details.sidebar
                                  .description_journalist
                            : profile.header.details.sidebar.description
                    }
                    limit={1200}
                />

                {!isJournalist && (
                    <Typo variant={'small'} Tag={'p'} className={'mt-xxxs'}>
                        Start your About with one sentence like this:{' '}
                        <em>Full name</em> is <em>position</em> and a{' '}
                        <em>
                            your job, e.g. mortgage broker, HR adviser, currency
                            specialist
                        </em>{' '}
                        at <em>firm</em>, based in <em>town or city</em>. For
                        example: “Jane Example is Managing Director and a
                        mortgage broker at Example Mortgages, based in Leeds.”
                        We add your media coverage count to the end of that
                        sentence automatically.
                    </Typo>
                )}

                <Divider />

                <Button
                    title={common.save}
                    loading={data.loading}
                    sticky={{ position: 'bottom' }}
                />
            </form>

            {!isJournalist && data.id && (
                <>
                    <Divider />
                    <FcaDetails
                        profileId={data.id}
                        slug={data.slug}
                        onDirtyChange={setFcaDirty}
                    />
                </>
            )}
        </Fragment>
    );
};
export default EditCompanyInfo;
